// ===========================================
// Parent RSVP panel (free events)
// ===========================================
// A family coming back to an event sees its OWN answer, with an Update button,
// rather than a blank form (#253, decision D4). Three read states are kept apart:
//   loading  -> spinner (the form is NOT shown, it could overwrite an answer)
//   error    -> message + Retry (same reason)
//   null     -> the family has not answered: the blank form
// After a submit the panel shows the RETURNED response, so a full event's
// downgrade to WAITLIST reads as the waitlist, never "attending".

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckIcon, HourglassTop as WaitIcon } from '@mui/icons-material';
import { useMyRsvp, useSubmitRsvp } from '../../hooks/useEventsParent';
import type { MyRsvp, RSVPResponse } from '../../api/events.api';

const RSVP_OPTIONS: { value: RSVPResponse; label: string }[] = [
  { value: 'ATTENDING', label: "Yes, I'm attending" },
  { value: 'NOT_ATTENDING', label: "No, I can't make it" },
  { value: 'MAYBE', label: 'Maybe' },
];

/** What a family reads about its stored answer. WAITLIST is the server's, not a choice. */
function rsvpSummaryLabel(response: RSVPResponse): string {
  switch (response) {
    case 'ATTENDING':
      return "Yes, I'm attending";
    case 'NOT_ATTENDING':
      return "No, I can't make it";
    case 'MAYBE':
      return 'Maybe';
    case 'WAITLIST':
      return "You're on the waitlist for this event";
    default:
      return 'Awaiting your answer';
  }
}

const BASE_GUEST_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

/** The stored count is always offered (0, or 9 to 20), so an Update can never silently shrink it. */
function guestOptionsFor(initial: number): number[] {
  return BASE_GUEST_OPTIONS.includes(initial)
    ? BASE_GUEST_OPTIONS
    : [...BASE_GUEST_OPTIONS, initial].sort((a, b) => a - b);
}

interface EditorProps {
  eventId: string;
  /** The stored answer, or null for a first answer. Seeds the state ONCE, on mount. */
  initial: MyRsvp | null;
  onDone: () => void;
  onCancel?: () => void;
}

function RsvpEditor({ eventId, initial, onDone, onCancel }: EditorProps) {
  // WAITLIST / PENDING are not choices: an Update from either re-asks "attending".
  const [response, setResponse] = useState<RSVPResponse>(
    initial && RSVP_OPTIONS.some((o) => o.value === initial.response) ? initial.response : 'ATTENDING'
  );
  const [guestCount, setGuestCount] = useState(initial?.guestCount ?? 1);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const submitRsvp = useSubmitRsvp();
  const guestOptions = guestOptionsFor(initial?.guestCount ?? 1);

  const handleSubmit = async () => {
    try {
      await submitRsvp.mutateAsync({
        eventId,
        // The trimmed note is sent even when empty: an empty note CLEARS the stored one,
        // an omitted one would keep it.
        data: { response, guestCount, notes: notes.trim() },
      });
      onDone();
    } catch {
      // useSubmitRsvp's onError already told the family; the form stays as typed.
    }
  };

  return (
    <Stack spacing={2}>
      <FormControl fullWidth size="small">
        <InputLabel id="rsvp-response-label">Your response</InputLabel>
        <Select
          labelId="rsvp-response-label"
          value={response}
          label="Your response"
          onChange={(e) => setResponse(e.target.value as RSVPResponse)}
        >
          {RSVP_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel id="guest-count-label">Guests attending</InputLabel>
        <Select
          labelId="guest-count-label"
          value={guestCount}
          label="Guests attending"
          onChange={(e) => setGuestCount(Number(e.target.value))}
        >
          {guestOptions.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        multiline
        rows={2}
        size="small"
        fullWidth
        placeholder="Dietary requirements, accessibility needs, etc."
      />

      <Stack direction="row" spacing={1}>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitRsvp.isPending}>
          {submitRsvp.isPending ? 'Saving…' : initial ? 'Update RSVP' : 'Submit RSVP'}
        </Button>
        {onCancel && (
          <Button variant="text" onClick={onCancel} disabled={submitRsvp.isPending}>
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

interface PanelProps {
  eventId: string;
  /** ISO instant; once past, the form is hidden and the answer is read-only. */
  registrationDeadline?: string | null;
}

export default function ParentRsvpPanel({ eventId, registrationDeadline }: PanelProps) {
  const { data: mine, isLoading, isError, isFetching, refetch } = useMyRsvp(eventId);
  const [editing, setEditing] = useState(false);

  const closed = !!registrationDeadline && new Date(registrationDeadline).getTime() < Date.now();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} aria-label="Loading your RSVP" />
      </Box>
    );
  }

  // No answer on screen yet and the read failed: nothing to show, and no form (it
  // could overwrite an answer we cannot see). A failed RE-read with an answer already
  // cached falls through and keeps that answer, with a quiet note below.
  if (mine === undefined) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" disabled={isFetching} onClick={() => refetch()}>
            {isFetching ? 'Retrying…' : 'Retry'}
          </Button>
        }
      >
        We could not load your RSVP.
      </Alert>
    );
  }

  if (mine === null && closed) {
    return <Alert severity="info">Registration for this event has closed.</Alert>;
  }

  if (mine === null || editing) {
    return (
      <RsvpEditor
        // Re-seed from the stored answer each time the editor opens.
        key={mine ? mine.updatedAt : 'new'}
        eventId={eventId}
        initial={mine}
        onDone={() => setEditing(false)}
        onCancel={mine ? () => setEditing(false) : undefined}
      />
    );
  }

  const waitlisted = mine.response === 'WAITLIST';
  const answered = mine.response !== 'PENDING' && !waitlisted;
  return (
    <Box>
      {isError && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          We could not refresh your RSVP, so this may be out of date.
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {answered ? <CheckIcon color="success" /> : <WaitIcon color="warning" />}
        <Typography variant="body1" fontWeight={600}>
          {answered ? `Your RSVP: ${rsvpSummaryLabel(mine.response)}` : rsvpSummaryLabel(mine.response)}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Guests: {mine.guestCount}
      </Typography>
      {mine.notes && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          Notes: {mine.notes}
        </Typography>
      )}
      {closed ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Registration has closed, so this answer can no longer be changed.
        </Typography>
      ) : (
        <Button variant="text" size="small" sx={{ mt: 1 }} onClick={() => setEditing(true)}>
          Update RSVP
        </Button>
      )}
    </Box>
  );
}
